import { z } from 'zod';
import weaponsJson from './weapons.json' with { type: 'json' };
import enemiesJson from './enemies.json' with { type: 'json' };
import chatJson from './chat.json' with { type: 'json' };

/** Regras de mira das armas (doc 07 §9) */
export const MiraSchema = z.enum([
  'nearest', 'nearest_curto', 'movimento', 'orbital', 'propria', 'zona_densa', 'espiral', 'aleatorio', 'aura',
]);

/** Baseline v0 (doc 08 §A.4): faixas de DPS validadas pelo schema — dado fora da faixa quebra o CI, não a run. */
export const WeaponSchema = z.object({
  id: z.string().regex(/^[a-z_]+$/),
  nome: z.string().min(1),
  mira: MiraSchema,
  dpsNv1: z.number().min(6).max(9),
  dpsNv5: z.number().min(18).max(26),
  passivoChave: z.string().regex(/^[a-z_]+$/),
  evolucao: z.string().regex(/^[a-z_]+$/),
  dpsEvolucaoMult: z.number().min(1.6).max(2.2),
});

export const WeaponsTableSchema = z.object({
  $comment: z.string().optional(),
  weapons: z.array(WeaponSchema).length(12), // 12 armas no lançamento (doc 03 §5.2)
});

export type Weapon = z.infer<typeof WeaponSchema>;

/** Tabela validada no load — import deste módulo já garante dados sãos. */
export const WEAPONS: readonly Weapon[] = WeaponsTableSchema.parse(weaponsJson).weapons;

/** Famílias de inimigos (doc 03 §6.1) */
export const EnemyFamilySchema = z.object({
  id: z.string().regex(/^[a-z_]+$/),
  nome: z.string().min(1),
  comportamento: z.enum(['perseguir', 'senoidal', 'rolar']),
  velocidade: z.number().min(20).max(200),
  dano: z.number().min(1).max(50),
  raioContato: z.number().min(8).max(32),
  hpFator: z.number().min(0.5).max(3),
  peso: z.number().int().min(1).max(100),
});

export const EnemiesTableSchema = z.object({
  $comment: z.string().optional(),
  families: z.array(EnemyFamilySchema).min(3),
});

export type EnemyFamily = z.infer<typeof EnemyFamilySchema>;
export const ENEMY_FAMILIES: readonly EnemyFamily[] = EnemiesTableSchema.parse(enemiesJson).families;

/** Chat da Sala da Escola — frases prontas seguras (doc 03 §3.4). Índice = phraseId. */
export const ChatTableSchema = z.object({
  $comment: z.string().optional(),
  phrases: z.array(z.string().min(1).max(24)).min(4),
});
export const CHAT_PHRASES: readonly string[] = ChatTableSchema.parse(chatJson).phrases;

export interface ChatIn {
  phraseId?: number;
  text?: string;
}
export interface ChatEntry {
  from: string;
  text: string;
}

/**
 * Sanitiza uma mensagem de chat (AC-M2-09, segurança infantil):
 *  - frase pronta (phraseId válido) → sempre permitida;
 *  - texto LIVRE → só se `freeChat` estiver ativado (dono da sala liberou); senão REJEITA (null);
 *  - qualquer outra coisa → null.
 */
export function sanitizeChat(from: string, msg: ChatIn, freeChat: boolean): ChatEntry | null {
  if (typeof msg.phraseId === 'number' && Number.isInteger(msg.phraseId)) {
    const frase = CHAT_PHRASES[msg.phraseId];
    if (frase) return { from, text: frase };
    return null; // phraseId fora da lista
  }
  if (typeof msg.text === 'string') {
    if (!freeChat) return null; // texto livre bloqueado por padrão
    const limpo = msg.text.replace(/\s+/g, ' ').trim().slice(0, 120);
    return limpo ? { from, text: limpo } : null;
  }
  return null;
}
